import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../../../components/Header';
import Icon from '../../../../components/AppIcon';
import {
  Button,
  Input,
  Select,
  Form,
  Row,
  Col,
  Card,
  message,
} from 'antd';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  createDevice,
  updateDevice,
  getDeviceById,
  validateLicense,
} from '../../../../api/deviceService';

import { getAllDeviceGroups } from '../../../../api/devicegroupService';

import type {
  DeviceCreateRequest,
  DeviceUpdateRequest,
  DeviceType,
} from '../../../../interface/requests/deviceRequest';
import type { DeviceDetailResponse } from '../../../../interface/responses/deviceResponse';
import type { DeviceGroupListResponse } from '../../../../interface/responses/devicegroupResponse';
import type { LicenseValidationResponse } from '../../../../interface/responses/licenseResponse';
import { ArrowLeftOutlined } from '@ant-design/icons';

type SectionFlags = {
  basic: boolean;
  network: boolean;
  advanced: boolean;
};

type DeviceFormValues = {
  device_type: DeviceType | '';
  device_name: string;
  device_serial: string;
  firmware_version: string;
  license_key?: string;
  group_ids?: number[];
  location: string;
  ip_address: string;
  user_agent: string;
  username: string;
  email: string;
  password: string;
  phone_number?: string;
};

type GroupOption = {
  value: number;
  label: string;
};

dayjs.extend(utc);
dayjs.extend(timezone);

const SectionHeader: React.FC<{
  title: string;
  icon: string;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ title, icon, isExpanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between p-4 hover:bg-muted transition-colors"
  >
    <div className="flex items-center space-x-3">
      <Icon name={icon} size={18} className="text-primary" />
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
    </div>
    <Icon name="ChevronDown" size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
  </button>
);

const DevicePreview: React.FC<{ 
  values?: Partial<DeviceFormValues>; 
  deviceId?: string;
  groupOptions?: GroupOption[];
}> = ({
  values,
  deviceId,
  groupOptions = [],
}) => {
  const safe = values || {};
  
  const selectedGroupNames = useMemo(() => {
    if (!safe.group_ids || safe.group_ids.length === 0) return '—';
    return safe.group_ids
      .map(id => groupOptions.find(opt => opt.value === id)?.label || `Group ${id}`)
      .join(', ');
  }, [safe.group_ids, groupOptions]);

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="Monitor" size={18} />
          <span>Device Preview</span>
        </h3>
        <p className="text-sm text-muted-foreground mt-1">Current device configuration snapshot</p>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Device Name</span>
          <span className="text-sm font-mono text-foreground">{safe.device_name || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Device Serial Number</span>
          <span className="text-sm font-mono text-foreground">{safe.device_serial || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Type</span>
          <span className="text-sm text-foreground">{safe.device_type || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Firmware</span>
          <span className="text-sm font-mono text-foreground">{safe.firmware_version || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Location</span>
          <span className="text-sm text-foreground">{safe.location || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">License Key</span>
          <span className="text-sm text-foreground">{safe.license_key ?? '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Groups</span>
          <span className="text-sm text-foreground">{selectedGroupNames}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Username</span>
          <span className="text-sm text-foreground">{safe.username ?? '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Phone Number</span>
          <span className="text-sm text-foreground">{safe.phone_number ?? '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Email</span>
          <span className="text-sm text-foreground">{safe.email ?? '—'}</span>
        </div>
      </div>
    </div>
  );
};

const AddEditDevice: React.FC = () => {
  const params = useParams();
  const id = params.id;
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [form] = Form.useForm<DeviceFormValues>();
  const [loading, setLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [licenseSearchLoading, setLicenseSearchLoading] = useState(false);
  const [licenseValidationResult, setLicenseValidationResult] = useState<LicenseValidationResponse | null>(null);

  const [expanded, setExpanded] = useState<SectionFlags & { license: boolean }>({
    basic: true,
    network: true,
    advanced: true,
    license: true,
  });

  const deviceTypeOptions = useMemo(
    () => [
      { value: 'Mobile', label: 'Mobile' },
      { value: 'STB', label: 'STB' },
    ],
    []
  );

  const initialValues: DeviceFormValues = useMemo(
    () => ({
      device_type: '',
      device_name: '',
      device_serial: '',
      firmware_version: '',
      license_key: '',
      group_ids: [],
      location: '',
      ip_address: '',
      user_agent: '',
      username: '',
      email: '',
      password: '',
      phone_number: '',
    }),
    []
  );

  useEffect(() => {
    const loadGroups = async () => {
      try {
        setGroupsLoading(true);        
        const response = await getAllDeviceGroups();
        const options: GroupOption[] = response.map(group => ({
          value: group.id,
          label: group.name,
        }));
        setGroupOptions(options);
        
      } catch (error: any) {
        message.error(`Failed to load device groups: ${error.message || 'Unknown error'}`);
      } finally {
        setGroupsLoading(false);
      }
    };

    loadGroups();
  }, []);

  const [loadedDeviceData, setLoadedDeviceData] = useState<any>(null);

  useEffect(() => {
    if (!isEditMode || !id) return;
    (async () => {
      try {
        setLoading(true);
        const detail: DeviceDetailResponse = await getDeviceById(Number(id));
        
        setLoadedDeviceData(detail);

        const groupIds = detail.groups?.map(group => group.id) ?? [];

        form.setFieldsValue({
          device_type: (detail.device_type as DeviceType) ?? '',
          device_name: detail.device_name ?? '',
          device_serial: detail.device_serial ?? '',
          firmware_version: detail.firmware_version ?? '',
          license_key: detail.license?.license_key ?? '',
          group_ids: groupIds,
          location: detail.location ?? '',
          ip_address: detail.license?.ip_address ?? '',
          user_agent: detail.license?.user_agent ?? '',
          username: detail.user?.username ?? '',
          email: detail.user?.email ?? '',
          password: '',
          phone_number: detail.user?.phone_number ?? '',
        });
        
      } catch (e: any) {
        console.error('❌ Failed to load device:', e);
        message.error(e?.message || 'Failed to load device');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEditMode, form]);

  const valuesForPreview = Form.useWatch([], form) as Partial<DeviceFormValues>;
  const selectedDeviceType = Form.useWatch('device_type', form);

  const normalizePhoneNumber = (value?: string | null) => {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  const onFinish = async (values: DeviceFormValues) => {
    setLoading(true);
    try {
      
      if (isEditMode && id) {
        const existingLicense = loadedDeviceData?.license;
        
        const updatePayload: DeviceUpdateRequest = {
          device_type: values.device_type as DeviceType,
          device_name: values.device_name,
          device_serial: values.device_serial,
          ...(selectedDeviceType === 'STB' && { firmware_version: values.firmware_version || '' }),
          group_ids: values.group_ids || [],
          location: values.location,
          license_key: values.license_key || '',
          allowed_domains: existingLicense?.allowed_domains || [],
          max_activations: existingLicense?.max_activations || 1,
          is_active: existingLicense?.is_active ?? true,
          expires_at: existingLicense?.expires_at || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          domain: existingLicense?.domain || 'wisemytech.com',
          ...(selectedDeviceType === 'STB' && values.ip_address && { ip_address: values.ip_address }),
          user_agent: values.user_agent || existingLicense?.user_agent || 'STB-Operator-App',
          username: values.username,
          email: values.email,
          phone_number: normalizePhoneNumber(values.phone_number),
          password: values.password && values.password.trim() !== '' ? values.password : null,
        };
        
        const result = await updateDevice(Number(id), updatePayload);
        message.success('Device updated successfully');
        
      } else {
        if (!licenseValidationResult?.data) {
          message.error('License validation is required for creating new devices');
          return;
        }
        
        const createPayload: DeviceCreateRequest = {
          device_type: values.device_type as DeviceType,
          device_name: values.device_name,
          device_serial: values.device_serial,
          ...(selectedDeviceType === 'STB' && { firmware_version: values.firmware_version || '' }),
          group_ids: values.group_ids || [],
          location: values.location,
          license_key: values.license_key || '',
          allowed_domains: typeof licenseValidationResult.data.allowed_domains === 'string' 
            ? JSON.parse(licenseValidationResult.data.allowed_domains)
            : licenseValidationResult.data.allowed_domains || [],
          max_activations: Math.max(licenseValidationResult.data.max_activations || 1, 1),
          is_active: licenseValidationResult.data.is_active ?? true,
          expires_at: licenseValidationResult.data.expires_at || null,
          domain: 'wisemytech.com',
          ...(selectedDeviceType === 'STB' && values.ip_address && { ip_address: values.ip_address }),
          user_agent: values.user_agent || 'STB-Operator-App',
          username: values.username || null,
          email: values.email || null,
          password: values.password || null,
          phone_number: normalizePhoneNumber(values.phone_number),
        };
        

        const result = await createDevice(createPayload);
        message.success('Device created successfully');
      }
      
      navigate('/device-management');
    } catch (err: any) {
      console.error('❌ Save failed:', err);
      message.error(err?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLicenseSearch = async () => {
    const licenseKey = form.getFieldValue('license_key');
    if (!licenseKey) {
      message.warning('Please enter a License Key to search');
      return;
    }

    setLicenseSearchLoading(true);
    setLicenseValidationResult(null);
    
    try {
      const result: LicenseValidationResponse = await validateLicense(licenseKey.toString(), 'wisemytech.com');
      
      console.log('✅ License validation result:', result);
      
      setLicenseValidationResult(result);
      
      if (result.success && result.data?.license_type) {
        const deviceType = result.data.license_type.toLowerCase() === 'mobile' ? 'Mobile' : 'STB';
        form.setFieldValue('device_type', deviceType);
        console.log('🎯 Device type set to:', deviceType);
      }
      
      if (result.success) {
        if (result.valid) {
          message.success('License validated successfully');
        } else {
          message.warning(result.message || 'License is not valid for this domain');
        }
      } else {
        message.error(result.message || 'License validation failed');
      }
      
    } catch (error: any) {
      console.error('❌ License validation failed:', error);
      message.error('License validation failed: ' + (error.message || 'Unknown error'));
      setLicenseValidationResult({
        success: false,
        valid: false,
        message: error.message || 'Unknown error'
      });
    } finally {
      setLicenseSearchLoading(false);
    }
  };

  const validateFormForSubmit = () => {
    if (isEditMode) {
      return true;
    }
    
    if (!licenseValidationResult || !licenseValidationResult.success) {
      message.error('Please validate the license key before saving');
      return false;
    }
    return true;
  };

  const handleFormSubmit = () => {
    if (!validateFormForSubmit()) {
      return;
    }
    form.submit();
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '—';
    const parsed = dayjs(dateString);
    if (!parsed.isValid()) return dateString;

    return parsed.tz('Asia/Kolkata').format('DD-MMM-YYYY hh:mm A [IST]');
  };

  const toggle = (key: keyof (SectionFlags & { license: boolean })) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const isDeviceTypeDisabled = licenseValidationResult?.success && licenseValidationResult?.data?.license_type;

  return (
    <div className="min-h-screen bg-background">
      <Header title="Operator Panel" />

      <main className="pt-15 px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {isEditMode ? 'Edit Device' : 'Add Device'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode
                  ? 'Modify device configuration and settings'
                  : 'Configure a new device for your network'}
              </p>
            </div>

            <button
              onClick={() => navigate('/device-management')}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              <ArrowLeftOutlined />
              Back
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-lg">
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={initialValues}
                  disabled={loading}
                  onFinish={onFinish}
                >
                  {/* LICENSE LOOKUP */}
                  <div className="p-6 border-b border-border">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-3">
                      <Icon name="Key" size={18} className="text-primary" />
                      <span>License Lookup</span>
                    </h3>
                    <Row gutter={16}>
                      <Col xs={24} md={16}>
                        <Form.Item
                          label="License Key"
                          name="license_key"
                          rules={[{ required: true, message: 'License Key is required' }]}
                        >
                          <Input 
                            placeholder="e.g. T1PW-GXD0-QVQ3-DU3P" 
                            disabled={isEditMode}
                          />
                        </Form.Item>
                      </Col>
                      {!isEditMode && (
                        <Col xs={24} md={8}>
                          <Form.Item label=" ">
                            <Button 
                              type="primary"
                              onClick={handleLicenseSearch}
                              loading={licenseSearchLoading}
                              disabled={loading}
                              className="w-full"
                            >
                              Search
                            </Button>
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* LICENSE INFORMATION SECTION */}
                  <SectionHeader
                    title="License Information"
                    icon="FileText"
                    isExpanded={expanded.license}
                    onToggle={() => toggle('license')}
                  />
                  {/* ✅ FIXED: Always render the content, just hide it with CSS */}
                  <div className={`p-6 ${!expanded.license ? 'hidden' : ''}`}>
                    {!isEditMode && licenseValidationResult ? (
                      <div className={`rounded-lg p-4 border ${
                        licenseValidationResult.success && licenseValidationResult.valid 
                          ? 'bg-green-50 border-green-200' 
                          : licenseValidationResult.success && !licenseValidationResult.valid
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <Icon 
                              name={licenseValidationResult.success && licenseValidationResult.valid ? "CheckCircle" : "AlertCircle"} 
                              size={20} 
                              className={
                                licenseValidationResult.success && licenseValidationResult.valid 
                                  ? "text-green-600" 
                                  : licenseValidationResult.success && !licenseValidationResult.valid
                                  ? "text-yellow-600"
                                  : "text-red-600"
                              } 
                            />
                            <span className={`font-semibold ${
                              licenseValidationResult.success && licenseValidationResult.valid 
                                ? "text-green-800" 
                                : licenseValidationResult.success && !licenseValidationResult.valid
                                ? "text-yellow-800"
                                : "text-red-800"
                            }`}>
                              {licenseValidationResult.success && licenseValidationResult.valid 
                                ? "Valid License" 
                                : licenseValidationResult.success && !licenseValidationResult.valid
                                ? "License Found (Domain Restricted)"
                                : "Invalid License"}
                            </span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            licenseValidationResult.data?.is_active 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {licenseValidationResult.data?.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <p className={`text-sm mb-4 ${
                          licenseValidationResult.success && licenseValidationResult.valid 
                            ? "text-green-700" 
                            : licenseValidationResult.success && !licenseValidationResult.valid
                            ? "text-yellow-700"
                            : "text-red-700"
                        }`}>
                          {licenseValidationResult.message}
                        </p>

                        {licenseValidationResult.data && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-medium text-gray-600">License Key</label>
                                <p className="text-sm font-mono bg-white text-black px-2 py-1 rounded border">
                                  {licenseValidationResult.data.license_key}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-600">License Type</label>
                                <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                  {licenseValidationResult.data.license_type || 'N/A'}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-600">Max Activations</label>
                                <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                  {licenseValidationResult.data.max_activations}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-medium text-gray-600">Expires At</label>
                                <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                  {licenseValidationResult.data.expires_at ? formatDate(licenseValidationResult.data.expires_at) : 'Never Expires'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {isEditMode && loadedDeviceData?.license ? (
                      <div className="rounded-lg p-4 border bg-blue-50 border-blue-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <Icon 
                              name="CheckCircle" 
                              size={20} 
                              className="text-blue-600"
                            />
                            <span className="font-semibold text-blue-800">
                              Current License Information
                            </span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            loadedDeviceData.license.is_active 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {loadedDeviceData.license.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-600">License Key</label>
                              <p className="text-sm font-mono bg-white text-black px-2 py-1 rounded border">
                                {loadedDeviceData.license.license_key}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">License Type</label>
                              <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                {loadedDeviceData.license.license_type || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Max Activations</label>
                              <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                {loadedDeviceData.license.max_activations}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Expires At</label>
                              <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                {loadedDeviceData.license.expires_at 
                                  ? formatDate(loadedDeviceData.license.expires_at) 
                                  : 'Never Expires'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Domain</label>
                              <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                {loadedDeviceData.license.domain || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">Allowed Domains</label>
                              <p className="text-sm bg-white text-black px-2 py-1 rounded border">
                                {Array.isArray(loadedDeviceData.license.allowed_domains) 
                                  ? loadedDeviceData.license.allowed_domains.join(', ') 
                                  : loadedDeviceData.license.allowed_domains || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {!isEditMode && !licenseValidationResult && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground text-center">
                          License details will appear here after searching
                        </p>
                      </div>
                    )}

                    {isEditMode && !loadedDeviceData?.license && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-muted-foreground text-center">
                          No license information available for this device
                        </p>
                      </div>
                    )}
                  </div>

                  {/* BASIC INFORMATION */}
                  <SectionHeader
                    title="Basic Information"
                    icon="Settings"
                    isExpanded={expanded.basic}
                    onToggle={() => toggle('basic')}
                  />
                  {/* ✅ FIXED: Always render, just hide with CSS */}
                  <div className={`p-6 ${!expanded.basic ? 'hidden' : ''}`}>
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Device Type"
                          name="device_type"
                          rules={[{ required: true, message: 'Device type is required' }]}
                        >
                          <Select
                            placeholder="Select device type"
                            options={deviceTypeOptions}
                            disabled={!!isDeviceTypeDisabled || loading}
                            allowClear={!isDeviceTypeDisabled}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Device Name"
                          name="device_name"
                          rules={[
                            { required: true, message: 'Device name is required' },
                            { max: 100, message: 'Maximum 100 characters allowed' }
                          ]}
                        >
                          <Input placeholder="e.g. Living Room Device" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Device Serial Number"
                          name="device_serial"
                          rules={[
                            { required: true, message: 'Device serial number is required' },
                            { max: 50, message: 'Maximum 50 characters allowed' },
                            { pattern: /^\S*$/, message: 'Device serial number cannot contain spaces' }
                          ]}
                        >
                          <Input placeholder="e.g. device001" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          label="Device Groups"
                          name="group_ids"
                          rules={[{ required: true }]}
                        >
                          <Select
                            mode="multiple"
                            placeholder="Select device groups"
                            options={[
                              {
                                value: 'select_all',
                                label: 'All Groups',
                              },
                              ...groupOptions
                            ]}
                            loading={groupsLoading}
                            allowClear
                            showSearch
                            filterOption={(input, option) => {
                              // Don't filter the "Select All" option
                              if (option?.value === 'select_all') return true;
                              return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
                            }}
                            onChange={(selectedValues) => {
                              if (selectedValues.includes('select_all')) {
                                // If "Select All" is chosen, select all actual groups
                                const allGroupIds = groupOptions.map(opt => opt.value);
                                form.setFieldValue('group_ids', allGroupIds);
                              } else {
                                // Normal selection
                                form.setFieldValue('group_ids', selectedValues);
                              }
                            }}
                            notFoundContent={groupsLoading ? 'Loading...' : 'No groups found'}
                            maxTagCount="responsive"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {selectedDeviceType === 'STB' && (
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="IP Address"
                            name="ip_address"
                            rules={[
                              { required: true, message: 'IP Address is required' },
                              { 
                                pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/, 
                                message: 'Please enter a valid IP address (e.g. 192.168.1.100)' 
                              }
                            ]}
                          >
                            <Input 
                              placeholder="e.g. 192.168.1.100" 
                              maxLength={15}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            label="Firmware Version"
                            name="firmware_version"
                            rules={[
                              { required: true, message: 'Firmware version is required' },
                              { max: 20, message: 'Maximum 20 characters allowed' }
                            ]}
                          >
                            <Input placeholder="e.g. 1.0.0" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {selectedDeviceType === 'STB' && (
                      <Row gutter={16}>
                        <Col xs={24}>
                          <Form.Item
                            label="Location"
                            name="location"
                            rules={[
                              { required: true, message: 'Location is required' }, 
                              { max: 100, message: 'Maximum 100 characters allowed' }, 
                              { min: 5, message: 'Minimum 5 characters required' }
                            ]}
                          >
                            <Input placeholder="e.g. Conference Room" />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </div>

                  {/* USER INFORMATION */}
                  {form.getFieldValue('device_type') === 'Mobile' && (
                    <>
                      <SectionHeader
                        title="User Information"
                        icon="User"
                        isExpanded={expanded.advanced}
                        onToggle={() => toggle('advanced')}
                      />
                      {/* ✅ FIXED: Always render, just hide with CSS */}
                      <div className={`p-6 ${!expanded.advanced ? 'hidden' : ''}`}>
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Username"
                              name="username"
                              rules={[
                                { required: true, message: 'Username is required' },
                                { min: 3, message: 'Username must be at least 3 characters' },
                                { max: 30, message: 'Username cannot exceed 30 characters' },
                                { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Username can only contain letters, numbers, hyphens, and underscores' }
                              ]}
                            >
                              <Input placeholder="e.g. john_doe" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Phone Number"
                              name="phone_number"
                              rules={[
                                { 
                                  pattern: /^[\+]?[1-9][\d]{0,15}$/, 
                                  message: 'Please enter a valid phone number' 
                                }
                              ]}
                            >
                              <Input 
                                placeholder="e.g. +1234567890" 
                                maxLength={20}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Email"
                              name="email"
                              rules={[
                                { required: true, message: 'Email is required' },
                                { 
                                  pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 
                                  message: 'Please enter a valid email format' 
                                }
                              ]}
                            >
                              <Input 
                                placeholder="e.g. john@example.com" 
                                maxLength={100}
                                showCount
                                type="email"
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              label="Password"
                              name="password"
                              rules={[
                                { 
                                  required: !isEditMode, 
                                  message: 'Password is required' 
                                }, 
                                { 
                                  min: 8, 
                                  message: 'Password must be at least 8 characters' 
                                },
                                { 
                                  max: 50, 
                                  message: 'Password must be at most 50 characters' 
                                },
                              ]}
                            >
                              <Input.Password 
                                placeholder={isEditMode ? "Leave empty to keep current password" : "Enter password"} 
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>

            {/* Right: Preview + Actions */}
            <div className="space-y-6">
              <DevicePreview 
                values={valuesForPreview} 
                deviceId={id} 
                groupOptions={groupOptions}
              />

              <Card className="bg-card border border-border rounded-lg">
                <div className="space-y-3">
                  <Button
                    type="primary"
                    block
                    loading={loading}
                    onClick={handleFormSubmit}
                  >
                    {isEditMode ? 'Update Device' : 'Add Device'}
                  </Button>
                  <Button block disabled={loading} onClick={() => navigate('/device-management')}>
                    Cancel
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddEditDevice;